import { useState, useEffect, useCallback, useRef } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { X, Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useApi } from "@/lib/api"

interface ShareeUser {
  userProfileId: number
  fullName: string
  primaryEmail: string
  functionalRolesId: number[]
}

interface ShareViewDialogProps {
  open: boolean
  onClose: () => void
  onSave: (isPublic: boolean, userProfileIds: number[], teamGuids: string[]) => void | Promise<void>
  isPublic: boolean
  shareeUserIds: number[]
  shareeTeamIds: string[]
  isOwner: boolean
}

export function ShareViewDialog({
  open,
  onClose,
  onSave,
  isPublic: initialIsPublic,
  shareeUserIds: initialUserIds,
  shareeTeamIds: initialTeamIds,
  isOwner,
}: ShareViewDialogProps) {
  const { reportsApi } = useApi()
  const [isPublic, setIsPublic] = useState(initialIsPublic)
  const [userIds, setUserIds] = useState<number[]>(initialUserIds)
  const [teamIds, setTeamIds] = useState<string[]>(initialTeamIds)
  const [allUsers, setAllUsers] = useState<ShareeUser[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsPublic(initialIsPublic)
    setUserIds(initialUserIds)
    setTeamIds(initialTeamIds)
  }, [initialIsPublic, initialUserIds, initialTeamIds])

  // Fetch users when dialog opens
  useEffect(() => {
    if (!open || allUsers.length > 0) return
    setIsLoadingUsers(true)
    reportsApi
      .get<ShareeUser[]>("/ReportSecurityView/GetListUserProfileInternal")
      .then((users) => setAllUsers(users ?? []))
      .catch((err) => console.error("Failed to load users:", err))
      .finally(() => setIsLoadingUsers(false))
  }, [open, reportsApi])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const filteredUsers = allUsers.filter((user) => {
    if (userIds.includes(user.userProfileId)) return false
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      user.fullName?.toLowerCase().includes(q) ||
      user.primaryEmail?.toLowerCase().includes(q)
    )
  })

  const selectedUsers = allUsers.filter((u) => userIds.includes(u.userProfileId))

  const handleSelectUser = (user: ShareeUser) => {
    setUserIds([...userIds, user.userProfileId])
    setSearchQuery("")
    setShowDropdown(false)
  }

  const handleRemoveUser = (id: number) => {
    setUserIds(userIds.filter((uid) => uid !== id))
  }

  const handleDone = async () => {
    await onSave(isPublic, userIds, teamIds)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Share Report</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Public toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Make report publicly accessible</Label>
            <div className="flex gap-1" role="group" aria-label="Public accessibility toggle">
              <button
                type="button"
                aria-pressed={!isPublic}
                className={cn(
                  "px-3 py-1 text-xs rounded-l-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
                  !isPublic
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border"
                )}
                onClick={() => setIsPublic(false)}
              >
                No
              </button>
              <button
                type="button"
                aria-pressed={isPublic}
                className={cn(
                  "px-3 py-1 text-xs rounded-r-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
                  isPublic
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border"
                )}
                onClick={() => setIsPublic(true)}
              >
                Yes
              </button>
            </div>
          </div>

          {/* Share with users */}
          {!isPublic && (
            <div className="space-y-2">
              <Label htmlFor="share-search" className="text-xs font-medium">Share with</Label>

              {/* Searchable people dropdown */}
              <div className="relative" ref={dropdownRef}>
                <div className="relative">
                  <Input
                    id="share-search"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-autocomplete="list"
                    aria-controls="share-user-list"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setShowDropdown(true)
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setShowDropdown(false)
                    }}
                    placeholder="-- Invite people or email --"
                    className="h-9 text-xs pr-8"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Toggle user list"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    <ChevronsUpDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                {showDropdown && (
                  <div id="share-user-list" role="listbox" aria-label="Users" className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md">
                    {isLoadingUsers ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground" role="status">Loading users...</div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No users found</div>
                    ) : (
                      filteredUsers.slice(0, 50).map((user) => (
                        <button
                          key={user.userProfileId}
                          role="option"
                          aria-selected={false}
                          onClick={() => handleSelectUser(user)}
                          className="w-full flex flex-col items-start px-3 py-2 text-xs hover:bg-accent transition-colors text-left focus-visible:outline-none focus-visible:bg-accent"
                        >
                          <span className="font-medium text-foreground">{user.fullName}</span>
                          {user.primaryEmail && (
                            <span className="text-muted-foreground italic">{user.primaryEmail}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Selected users list */}
              {selectedUsers.length > 0 && (
                <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                  {selectedUsers.map((user) => (
                    <div
                      key={user.userProfileId}
                      className="flex items-center justify-between px-2 py-1.5 bg-muted/50 rounded text-xs"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{user.fullName}</span>
                        {user.primaryEmail && (
                          <span className="text-muted-foreground">{user.primaryEmail}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveUser(user.userProfileId)}
                        aria-label={`Remove ${user.fullName}`}
                        className="text-muted-foreground hover:text-destructive ml-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded p-0.5"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" className="text-xs" onClick={handleDone}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
